import {
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
  GSButton,
  GSFileSelect,
  GSHTMLViewer,
  GSImage,
  GSInfoGroup,
  GSInput,
  GSItemList,
  useMediaQuery,
} from "golfstatus_react_components";
import {
  faEnvelopeCircleCheck,
  faEnvelopeOpen,
  faExclamationCircle,
  faHand,
  faHandPointer,
  faPaperPlane,
  faSearch,
  faTimesCircle,
  faUserPen,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getDateTimeOn, isV3WithinLastHours } from "../helpers/Dates";
import { formatByteSize } from "../helpers/Utilities";
import TournamentMessageInfo from "../components/tournament/tournament-messages/tournament-message-info";
import TournamentMessageRecipientGroupsList from "../components/tournament/tournament-messages/tournament-message-recipient-groups-list";
import TournamentMessageTournamentLineItem from "../components/tournament/tournament-messages/tournament-message-tournament-line-item";

export const RECIPIENT_GROUP_LABELS = {
  sponsors: "Sponsors",
  players: "Players & Teams",
  waitlist: "Waitlist",
  customers: "Customers",
  donors: "Donors",
};

export const getMessageCountLines = ({
  status,
  uniqueSelectedCount = 0,
  tournamentsCount = 0,
  selectedGroupsCount = 0,
} = {}) => {
  const line = (count, label) =>
    `${count} ${count === 1 ? label : `${label}s`}`;
  const lines = [];
  if (status === "sent") {
    lines.push(line(uniqueSelectedCount, "Total Recipient"));
  }
  lines.push(line(tournamentsCount, "Tournament"));
  lines.push(line(selectedGroupsCount, "Group"));
  return lines;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

export const RecipientModeBanner = ({
  groupLabel,
  groupType,
  isIncludeAll,
}) => {
  const waitlistLabel = groupType === "waitlist" ? "Waitlist Members" : null;

  const infoSection = {
    sectionItems: [
      {
        type: "primary full-width",
        value: isIncludeAll
          ? `Sending to All ${waitlistLabel ?? groupLabel}`
          : `Sending to Selected ${waitlistLabel ?? capitalize(groupType)}`,
      },
      {
        type: "secondary full-width",
        value: isIncludeAll
          ? `All ${(waitlistLabel ?? groupLabel).toLowerCase()} will receive this message.`
          : `Only the selected ${(waitlistLabel ?? groupLabel).toLowerCase()} will receive this message.`,
      },
    ],
  };

  const iconSection = {
    sectionItems: [
      {
        type: "primary",
        value: (
          <FontAwesomeIcon
            className="banner-icon"
            icon={isIncludeAll ? faPaperPlane : faUserPen}
          />
        ),
      },
    ],
  };

  return (
    <div
      className={`recipient-mode-banner${
        isIncludeAll ? " is-include-all" : ""
      }`}
    >
      <GSInfoGroup
        type="list-item x-large-pad"
        dataGroups={[
          { sections: [iconSection] },
          { fit: "stretch", sections: [infoSection] },
        ]}
      />
    </div>
  );
};

const RECIPIENT_STATUS_MAP = {
  sent: {
    label: "Sent",
    icon: faPaperPlane,
    color: "light-grey",
    timestampKey: "sentAt",
  },
  deferred: {
    label: "Delivery Delayed",
    icon: faHand,
    color: "light-grey",
    timestampKey: "sentAt",
  },
  delivered: {
    label: "Delivered",
    icon: faEnvelopeCircleCheck,
    color: "green",
    timestampKey: "deliveredAt",
  },
  opened: {
    label: "Opened",
    icon: faEnvelopeOpen,
    color: "green",
    timestampKey: "deliveredAt",
  },
  clicked: {
    label: "Clicked",
    icon: faHandPointer,
    color: "green",
    timestampKey: "deliveredAt",
  },
  bounced: {
    label: "Email Bounced",
    icon: faExclamationCircle,
    color: "orange",
    timestampKey: "sentAt",
  },
  dropped: {
    label: "Email Dropped",
    icon: faExclamationCircle,
    color: "orange",
    timestampKey: "sentAt",
  },
  spam_reported: {
    label: "Spam Report",
    icon: faExclamationCircle,
    color: "orange",
    timestampKey: "sentAt",
  },
  failed: {
    label: "Failed",
    icon: faExclamationCircle,
    color: "orange",
    timestampKey: "sentAt",
  },
};

const getRecipientStatusInfo = (recipient) => {
  const key = recipient?.status;
  if (!key) return RECIPIENT_STATUS_MAP.sent;
  return RECIPIENT_STATUS_MAP[key] ?? RECIPIENT_STATUS_MAP.sent;
};

export const RecipientListItem = ({
  recipient,
  channel,
  groupType,
  messageStatus,
}) => {
  const { isPhone } = useMediaQuery();
  const isSelectable = recipient.selectable !== false;
  const isEmail = channel === "email";
  const emptyContactLabel = isEmail ? "No Email Address" : "No GolfStatus App";
  const isSponsor = groupType === "sponsors";
  const showTeamName = groupType === "players" && recipient.teamName;
  const showSponsorName = isSponsor && recipient.sponsorName;
  const showSponsorLogo = isSponsor && recipient.sponsorLogoUrl && !isPhone();
  const className = isSelectable
    ? "recipient-row"
    : "recipient-row recipient-unselectable";

  const showStatus = isEmail && messageStatus === "sent";
  const statusInfo = showStatus ? getRecipientStatusInfo(recipient) : null;
  const statusTimestamp = statusInfo
    ? recipient?.[statusInfo.timestampKey] ?? recipient?.sentAt
    : null;
  const timestampPrefix =
    statusInfo?.timestampKey === "deliveredAt" ? "Delivered at" : "Sent at";

  const statusAlign = isPhone() ? "flex-start" : "flex-end";
  const statusGroup = showStatus
    ? {
        sections: [
          {
            gap: "medium-gap",
            sectionItems: [
              ...(statusTimestamp
                ? [
                    {
                      type: "secondary no-wrap",
                      sectionItemStyle: {
                        ...defaultLayouts.hStack.align(statusAlign, "center"),
                        ...defaultLayouts.fullWidth,
                      },
                      value: `${timestampPrefix} ${getDateTimeOn(
                        statusTimestamp,
                      )}`,
                    },
                  ]
                : []),
              {
                type: " no-wrap",
                sectionItemStyle: {
                  ...defaultLayouts.hStack.align(statusAlign, "center"),
                  ...defaultLayouts.fullWidth,
                },
                value: (
                  <GSButton
                    title={statusInfo.label}
                    buttonIcon={statusInfo.icon}
                    type={`secondary status pill ${statusInfo.color}`}
                    tooltip={recipient?.errorMessage || undefined}
                  />
                ),
              },
            ],
          },
        ],
      }
    : null;

  const sponsorLogoGroup = showSponsorLogo
    ? {
        sections: [
          {
            sectionItems: [
              {
                type: "primary",
                value: (
                  <GSImage src={recipient.sponsorLogoUrl} size="wide small" />
                ),
              },
            ],
          },
        ],
      }
    : null;

  return (
    <div className={className}>
      <GSInfoGroup
        listType="mobile-vertical"
        dataGroups={[
          ...(sponsorLogoGroup ? [sponsorLogoGroup] : []),
          {
            fit: "stretch",
            sections: [
              ...(showSponsorName
                ? [
                    {
                      sectionItems: [
                        {
                          type: "primary full-width sponsor-name",
                          value: recipient.sponsorName,
                        },
                      ],
                    },
                  ]
                : []),
              {
                sectionItems: [
                  {
                    type: `${
                      showSponsorName ? "secondary" : "primary"
                    } full-width`,
                    value:
                      `${recipient.firstName ?? ""} ${recipient.lastName ??
                        ""}`.trim() || "—",
                  },
                  {
                    type: "secondary full-width",
                    value:
                      !isEmail && !isSelectable
                        ? emptyContactLabel
                        : recipient.email || emptyContactLabel,
                  },
                  ...(showTeamName
                    ? [
                        {
                          type: "secondary full-width team-name",
                          value: recipient?.teamName,
                        },
                      ]
                    : []),
                ],
              },
            ],
          },
          ...(statusGroup ? [statusGroup] : []),
        ]}
      />
    </div>
  );
};

export const RecipientsDisclaimer = ({ channel, isSent = false }) => {
  const isEmail = channel === "email";

  const getMessage = () => {
    if (isSent) {
      return isEmail
        ? "Emails are on their way - delivery times may vary depending on email provider."
        : "Notifications are on their way - delivery times may vary.";
    }
    return isEmail
      ? "Emails are sent once per email address, even if selected in multiple groups."
      : "Notifications are sent once per user, even if selected in multiple groups.";
  };

  return (
    <div
      style={{
        ...defaultPadding.largePad,
        ...defaultBorders.mediumLargeBorderRadius,
        backgroundColor: isSent ? golfstatusColors.green50 : golfstatusColors.cyan50,
        whiteSpace: "normal",
      }}
    >
      <div
        style={{
          ...defaultLayouts.hStack.align("unset", "center"),
          ...defaultGapping.mediumLargeGap,
          ...defaultTypography.xSmallBody,
          color: isSent ? undefined : golfstatusColors.cyan600,
        }}
      >
        <FontAwesomeIcon icon={faPaperPlane} />
        <span style={{ opacity: isSent ? defaultTypography.secondary.opacity : undefined }}>
          {getMessage()}
        </span>
      </div>
    </div>
  );
};

export const messageDetailsForm = (context, currentMessage) => {
  if (!context) return [];

  const {
    selectedTournaments = [],
    recipientGroups = [],
    selectedGroups = [],
    recipientCount = 0,
    displayStatus,
    isLocked,
    getRecipientsActions,
    getContentActions,
    getEmptyRecipientsMessage,
    getEmptyContentMessage,
    onViewRecipientGroup,
    onComposeMessage,
    onDeleteMessage,
    onSelectRecipients,
  } = context;

  const getRecipientsForTournament = (tournament) =>
    recipientGroups?.filter?.((r) => r?.tournamentId === tournament?.id) ?? [];

  const messageInfo = {
    type: "vertical",
    fields: [
      {
        customView: true,
        value: (
          <TournamentMessageInfo
            currentMessage={currentMessage}
            displayStatus={displayStatus}
            recipientCount={recipientCount}
            isLocked={isLocked}
            onComposeMessage={onComposeMessage}
            onDeleteMessage={onDeleteMessage}
          />
        ),
      },
    ],
  };

  const isRecentlySent =
    currentMessage?.status === "sent" &&
    isV3WithinLastHours(currentMessage?.sentAt, 2);

  const getDisclaimerFields = () => {
    if (isRecentlySent) {
      return [
        {
          customView: true,
          value: (
            <RecipientsDisclaimer channel={currentMessage?.channel} isSent={true} />
          ),
        },
      ];
    }
    if (selectedGroups.length > 1 && !isLocked) {
      return [
        {
          customView: true,
          value: <RecipientsDisclaimer channel={currentMessage?.channel} />,
        },
      ];
    }
    return [];
  };

  const hasExcludedTournaments = selectedTournaments.some((t) => t?.excluded);

  const recipientsSection = {
    type: "vertical xx-large-gap no-wrap",
    title: "Recipients",
    sectionActions: getRecipientsActions?.() ?? [],
    fields: [
      ...getDisclaimerFields(),
      {
        customView: true,
        value:
          selectedGroups.length > 0 || hasExcludedTournaments ? (
            <GSItemList
              type="vertical large-gap"
              items={selectedTournaments}
              listItem={(tournament) => (
                <TournamentMessageRecipientGroupsList
                  tournament={tournament}
                  recipients={getRecipientsForTournament(tournament)}
                  selectionView={false}
                  isLocked={isLocked}
                  onView={isLocked ? onViewRecipientGroup : undefined}
                  onSelectRecipients={onSelectRecipients}
                  messageType={
                    currentMessage?.channel === "email" ? "email" : "app notification"
                  }
                />
              )}
            />
          ) : (
            <GSItemList
              type="vertical grey-empty-list rounded-empty-list"
              items={[]}
              emptyMessage={getEmptyRecipientsMessage?.()}
              listItem={() => null}
            />
          ),
      },
    ],
  };

  const hasMessageContent =
    currentMessage?.subject ||
    currentMessage?.bodyHtml ||
    currentMessage?.bodyText ||
    (currentMessage?.emailAttachments &&
      currentMessage.emailAttachments.length > 0);

  const messageContentSection = {
    type: "vertical xx-large-gap no-wrap",
    title: "Message Details",
    sectionActions: isLocked ? [] : getContentActions?.() ?? [],
    fields: hasMessageContent
      ? [
          {
            label: "SUBJECT",
            value: currentMessage?.subject || "—",
          },
          {
            label: "MESSAGE",
            customView: currentMessage?.channel === "email",
            align: "align-start",
            value:
              currentMessage?.channel === "email" ? (
                <GSHTMLViewer html={currentMessage?.bodyHtml || ""} />
              ) : (
                currentMessage?.bodyText || "—"
              ),
          },
          ...(currentMessage?.channel === "email" &&
          currentMessage?.emailAttachments?.length > 0
            ? [
                {
                  label: "ATTACHMENTS",
                  customView: true,
                  value: (
                    <GSFileSelect
                      sourceList={currentMessage.emailAttachments.map((a) => ({
                        url: a.url,
                        name: `${a.filename} (${formatByteSize(a.byteSize)})`,
                        type: a.contentType,
                        size: a.byteSize,
                      }))}
                      disabled={true}
                    />
                  ),
                },
              ]
            : []),
          ...(currentMessage?.channel === "email"
            ? [
                {
                  label: "REPLY TO",
                  value: currentMessage?.replyTo || "—",
                },
              ]
            : []),
        ]
      : [
          {
            customView: true,
            value: (
              <GSItemList
                type="vertical grey-empty-list rounded-empty-list"
                items={[]}
                emptyMessage={getEmptyContentMessage?.()}
                listItem={() => null}
              />
            ),
          },
        ],
  };

  const isLegacySentMessage =
    currentMessage?.status === "sent" &&
    !currentMessage?.channel &&
    (recipientGroups?.length ?? 0) === 0;

  return isLegacySentMessage
    ? [messageInfo, messageContentSection]
    : [messageInfo, recipientsSection, messageContentSection];
};

const selectedRowStyle = {
  ...defaultLayouts.hStack.align("space-between", "center"),
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
  ...defaultPadding.xLargePad,
  ...defaultBorders.mediumLargeBorderRadius,
  backgroundColor: golfstatusColors.grey50,
};

const selectedRowBodyStyle = {
  flex: 1,
  minWidth: 0,
};

const availableRowStyle = {
  ...defaultLayouts.fullWidth,
  ...defaultPadding.xLargePad,
  ...defaultBorders.mediumLargeBorderRadius,
  border: `1px solid ${golfstatusColors.grey200}`,
};

export const tournamentSelectForm = (context) => {
  if (!context) return [];

  const {
    selectedTournaments = [],
    availableTournaments = [],
    ownerTournamentID,
    name = "",
    setName,
    submitSearch,
    clearSearch,
    filter,
    addTournament,
    removeTournament,
    getEmptySearchMessage,
    getEmptyListMessage,
    tournamentsLoading,
  } = context;

  const renderSelectedRow = (tournament) => {
    const isOwner = tournament?.id === ownerTournamentID;
    return (
      <div style={selectedRowStyle}>
        <div style={selectedRowBodyStyle}>
          <TournamentMessageTournamentLineItem tournament={tournament} />
        </div>
        {!isOwner && (
          <GSButton
            type="transparent"
            buttonIcon={faTimesCircle}
            onClick={(e) => {
              removeTournament?.(tournament);
              e?.stopPropagation?.();
            }}
          />
        )}
      </div>
    );
  };

  return [
    {
      type: "vertical xx-large-gap no-wrap",
      fields: [
        {
          value: (
            <GSItemList
              type="vertical medium-gap"
              items={selectedTournaments}
              listItem={renderSelectedRow}
            />
          ),
        },
        {
          label: "Available Tournaments",
          value: (
            <GSInput
              textValue={name}
              onChange={(e) => setName?.(e.target.value)}
              onSubmit={submitSearch}
              placeholder="Search Tournaments..."
              leftIcon={faSearch}
              rightIcon={faTimesCircle}
              rightIconClick={clearSearch}
            />
          ),
        },
        {
          value: (
            <GSItemList
              type="vertical medium-gap selectable grey-empty-list rounded-empty-list"
              items={availableTournaments}
              itemSelected={addTournament}
              itemStyle={{ ...defaultBorders.mediumLargeBorderRadius }}
              loading={tournamentsLoading}
              loadingMainText="Loading Tournaments..."
              emptyMessage={
                filter?.name
                  ? getEmptySearchMessage?.()
                  : getEmptyListMessage?.()
              }
              listItem={(tournament) => (
                <div style={availableRowStyle}>
                  <TournamentMessageTournamentLineItem
                    tournament={tournament}
                  />
                </div>
              )}
            />
          ),
        },
      ],
    },
  ];
};
